
import { useState } from "react";
import { Profile } from "@/types/database.types";
import { ShieldCheck, ShieldX } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";

interface AdminToggleProps {
  user: Profile;
  isAdmin: boolean;
  isProtectedEmail: boolean;
  onAdminStatusChange: () => void;
}

export const AdminToggle = ({ 
  user, 
  isAdmin,
  isProtectedEmail,
  onAdminStatusChange 
}: AdminToggleProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const toggleAdminStatus = async () => {
    setIsLoading(true);
    
    try {
      // DEMO MODE: Just show a notification that all users have admin access
      console.log(`DEMO MODE: Admin toggle clicked for user ${user.id}`, user.email);
      
      setTimeout(() => {
        toast({
          title: "Demo Mode Active",
          description: "In demo mode, all users have full admin access. Admin status changes are simulated.",
        });
        
        // Simulate success by calling the callback
        onAdminStatusChange();
        setIsLoading(false);
      }, 500);
      
    } catch (error: any) {
      console.error("Error in demo mode:", error);
      toast({
        title: "Demo Error",
        description: "An error occurred in the demo. Please try again.",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  return (
    <Button 
      variant={isAdmin ? "outline" : "glass-primary"}
      size="sm" 
      className={`w-full ${isAdmin ? 'border-red-200 hover:bg-red-50 text-red-600' : ''}`}
      onClick={toggleAdminStatus}
      disabled={isLoading || isProtectedEmail}
    >
      {isAdmin ? (
        <>
          <ShieldX className="mr-2 h-4 w-4 text-red-500" />
          Revoke Admin
        </>
      ) : (
        <>
          <ShieldCheck className="mr-2 h-4 w-4" />
          Make Admin
        </>
      )}
    </Button>
  );
};
